// PlaceAutocompleteInput: input de dirección con autocompletado de Google Places.
//
// Muestra las sugerencias que devuelve Google mientras se escribe y, al elegir
// una opción, resuelve lat/lng y las entrega vía onSelect. Reutilizable y
// autónomo (carga el script de Google con la librería `places`).
//
// Props:
//   value       (string)  texto actual (controlado por el padre)
//   onChange    (fn)      recibe el texto tecleado (para actualizar el padre)
//   onSelect    (fn)      recibe { description, lat, lng } al elegir una opción
//   disabled    (bool)
//   size        ("sm"|undefined) tamaño del input (bootstrap)
//   placeholder (string)
import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useLoadScript } from "@react-google-maps/api"
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete"
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from "@reach/combobox"

import config from "../../constants/settings"

const LIBRARIES = ["places"]

// Autocomplete interno: solo se monta cuando el script de Google está cargado
// (usePlacesAutocomplete requiere window.google.maps.places).
const Inner = ({ value, onChange, onSelect, disabled, size, placeholder }) => {
  const {
    value: acValue,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: { componentRestrictions: { country: "mx" } },
    debounce: 300,
    defaultValue: value || "",
  })

  // Sincroniza el texto externo (p. ej. al abrir la edición) con el hook.
  useEffect(() => {
    if ((value || "") !== acValue) setValue(value || "", false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleInput = (e) => {
    const v = e.target.value
    setValue(v)
    onChange?.(v)
  }

  const handleSelect = async (address) => {
    setValue(address, false)
    clearSuggestions()
    onChange?.(address)
    try {
      const results = await getGeocode({ address })
      const { lat, lng } = await getLatLng(results[0])
      onSelect?.({ description: address, lat, lng })
    } catch (_e) {
      // Si falla el geocode de la opción elegida, al menos se conserva el texto.
      onSelect?.({ description: address, lat: null, lng: null })
    }
  }

  return (
    <Combobox onSelect={handleSelect} openOnFocus>
      <ComboboxInput
        value={acValue}
        onChange={handleInput}
        disabled={disabled}
        placeholder={placeholder}
        className={`form-control${size === "sm" ? " form-control-sm" : ""}`}
        autoComplete="off"
      />
      <ComboboxPopover className="places" style={{ zIndex: 3000 }}>
        <ComboboxList>
          {status === "OK" && data.map((item) => (
            <ComboboxOption key={item.place_id} value={item.description} className="place" />
          ))}
        </ComboboxList>
      </ComboboxPopover>
    </Combobox>
  )
}

Inner.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  placeholder: PropTypes.string,
}

const PlaceAutocompleteInput = (props) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.google.secret,
    libraries: LIBRARIES,
    language: "es",
    region: "mx",
  })

  // Mientras carga el script, un input controlado plano (permite teclear; sin
  // sugerencias todavía). Así el campo nunca queda bloqueado.
  if (!isLoaded) {
    return (
      <input
        className={`form-control${props.size === "sm" ? " form-control-sm" : ""}`}
        value={props.value || ""}
        disabled={props.disabled}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange?.(e.target.value)}
      />
    )
  }
  return <Inner {...props} />
}

PlaceAutocompleteInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  placeholder: PropTypes.string,
}

export default PlaceAutocompleteInput
